import React, { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FolderOpen, Eye, Download, Lock, Upload, Check, X, Archive, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLE = { "Vérifié": "bg-emerald-100 text-emerald-700", "Refusé": "bg-red-100 text-red-700", "Archivé": "bg-slate-200 text-slate-400", "Générée": "bg-indigo-100 text-indigo-700", "Déposé": "bg-amber-100 text-amber-700" };

export default function DocumentsRegistry() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration");
  const canView = hasRole("super_admin", "direction", "administration", "coordinateur", "finance");
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [fType, setFType] = useState("all");
  const [fSensible, setFSensible] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("deposited_at");
  const [sortDir, setSortDir] = useState("desc");
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [up, setUp] = useState({ open: false, name: "", type: "courrier", file: null });
  const [reject, setReject] = useState({ open: false, id: null, text: "" });
  const fileRef = useRef(null);

  const load = () => {
    const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
    if (fType !== "all") params.type = fType;
    if (fSensible !== "all") params.sensible = fSensible;
    if (fSource !== "all") params.source = fSource;
    if (search.trim()) params.search = search.trim();
    for (const k of ["student_id", "family_id", "parent_id", "teacher_id", "admission_id"]) if (sp.get(k)) params[k] = sp.get(k);
    api.get("/registry", { params }).then((r) => { setRows(r.data.documents); setMeta({ total: r.data.total, pages: r.data.pages || 1 }); }).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  };
  useEffect(() => { setPage(1); }, [fType, fSensible, fSource, search, pageSize, sortBy, sortDir]);
  useEffect(() => { load(); }, [fType, fSensible, fSource, search, page, pageSize, sortBy, sortDir]); // eslint-disable-line
  useEffect(() => { api.get("/registry/types").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);

  const openFile = async (url) => {
    try {
      const r = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doUpload = async () => {
    if (!up.name.trim() || !up.file) { toast.error(t("fillRequired")); return; }
    const fd = new FormData(); fd.append("name", up.name); fd.append("type", up.type); fd.append("file", up.file); fd.append("is_test", "false");
    try { await api.post("/registry/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("save")); setUp({ open: false, name: "", type: "courrier", file: null }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const act = async (row, path) => {
    if (row.source !== "doc") { toast.error(t("actionNativeOnly")); return; }
    try { await api.patch(`/registry/documents/${row.doc_id}/${path}`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const submitReject = async () => {
    if (!reject.text.trim()) { toast.error(t("reasonRequired")); return; }
    try { await api.patch(`/registry/documents/${reject.id}/reject`, { reason: reject.text.trim() }); toast.success(t("save")); setReject({ open: false, id: null, text: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="documents-registry">
      {!canView ? <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div> : (<>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3"><FolderOpen className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("documentRegistry")}</h1></div>
        {canManage && <Button className="gap-2" data-testid="upload-doc-btn" onClick={() => setUp({ open: true, name: "", type: "courrier", file: null })}><Upload className="h-4 w-4" />{t("uploadDocument")}</Button>}
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="registry-filters">
        <Input placeholder={t("searchDocs")} value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="registry-search" />
        <Select value={fType} onValueChange={setFType}><SelectTrigger className="w-52" data-testid="filter-doc-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allTypes")}</SelectItem>{cats.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent></Select>
        <Select value={fSensible} onValueChange={setFSensible}><SelectTrigger className="w-40" data-testid="filter-sensible"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allDocs")}</SelectItem><SelectItem value="true">{t("sensitive")}</SelectItem><SelectItem value="false">{t("nonSensitive")}</SelectItem></SelectContent></Select>
        <Select value={fSource} onValueChange={setFSource}><SelectTrigger className="w-44" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allSources")}</SelectItem><SelectItem value="doc">{t("uploaded")}</SelectItem><SelectItem value="admission">{t("admissions")}</SelectItem><SelectItem value="photo">{t("photoEleve")}</SelectItem><SelectItem value="card">{t("generated")}</SelectItem></SelectContent></Select>
        <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="w-40" data-testid="sort-by"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="deposited_at">{t("date")}</SelectItem><SelectItem value="type">{t("dossierType")}</SelectItem><SelectItem value="status">{t("status")}</SelectItem><SelectItem value="sensible">{t("sensitive")}</SelectItem><SelectItem value="source">{t("source")}</SelectItem></SelectContent></Select>
        <Select value={sortDir} onValueChange={setSortDir}><SelectTrigger className="w-32" data-testid="sort-dir"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="desc">↓ Desc</SelectItem><SelectItem value="asc">↑ Asc</SelectItem></SelectContent></Select>
      </div>

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>{t("category")}</TableHead><TableHead>{t("source")}</TableHead>
            <TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{t("noData")}</TableCell></TableRow> :
              rows.map((r) => (
                <TableRow key={r.id} data-testid="registry-row">
                  <TableCell className="font-medium text-slate-800">{r.name}
                    {r.sensible && <Badge className="ml-2 bg-red-100 text-red-700 border-0 gap-1" data-testid="sensitive-badge"><ShieldAlert className="h-3 w-3" />{t("sensitive")}</Badge>}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.category}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-500 border-0 text-xs">{r.source}</Badge></TableCell>
                  <TableCell><Badge className={`border-0 text-xs ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="ghost" className="gap-1 h-8" data-testid={`preview-${r.id}`} onClick={() => openFile(r.preview_url)}><Eye className="h-4 w-4" />{t("preview")}</Button>
                      <Button size="sm" variant="ghost" className="gap-1 h-8" data-testid={`download-${r.id}`} onClick={() => openFile(r.download_url)}><Download className="h-4 w-4" />{t("download")}</Button>
                      {r.sensible && r.original_url && <Button size="sm" variant="ghost" className="gap-1 h-8 text-amber-700" data-testid={`original-${r.id}`} onClick={() => openFile(r.original_url)}><Lock className="h-4 w-4" />{t("downloadOriginal")}</Button>}
                      {canManage && r.source === "doc" && r.status !== "Archivé" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-8 text-emerald-700" data-testid={`verify-${r.id}`} onClick={() => act(r, "verify")}><Check className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 text-red-700" data-testid={`reject-${r.id}`} onClick={() => setReject({ open: true, id: r.doc_id, text: "" })}><X className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 text-slate-500" data-testid={`archive-${r.id}`} onClick={() => act(r, "archive")}><Archive className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-2" data-testid="registry-pagination">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span data-testid="registry-total">{meta.total} {t("documents").toLowerCase()}</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-24" data-testid="page-size"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="25">25</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="prev-page" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</Button>
          <span className="text-sm text-slate-600" data-testid="page-indicator">{page} / {meta.pages}</span>
          <Button size="sm" variant="outline" data-testid="next-page" disabled={page >= meta.pages} onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}>›</Button>
        </div>
      </div>

      <Dialog open={up.open} onOpenChange={(o) => setUp((u) => ({ ...u, open: o }))}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader><DialogTitle>{t("uploadDocument")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">{t("name")}</label><Input data-testid="upload-name" value={up.name} onChange={(e) => setUp((u) => ({ ...u, name: e.target.value }))} /></div>
            <div><label className="text-xs text-slate-500">{t("category")}</label>
              <Select value={up.type} onValueChange={(v) => setUp((u) => ({ ...u, type: v }))}>
                <SelectTrigger data-testid="upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}{c.sensible ? " 🔒" : ""}</SelectItem>)}</SelectContent></Select></div>
            <input ref={fileRef} type="file" data-testid="upload-file" onChange={(e) => setUp((u) => ({ ...u, file: e.target.files?.[0] || null }))} />
          </div>
          <DialogFooter><Button data-testid="upload-save-btn" onClick={doUpload}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reject.open} onOpenChange={(o) => setReject((r) => ({ ...r, open: o }))}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>{t("rejectDoc")}</DialogTitle></DialogHeader>
          <Input data-testid="reject-reason" placeholder={t("rejectReasonLabel")} value={reject.text} onChange={(e) => setReject((r) => ({ ...r, text: e.target.value }))} />
          <DialogFooter><Button data-testid="reject-confirm-btn" onClick={submitReject}>{t("confirmReason")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
